import { useState } from 'react';
import { Camera, Mail, MapPinHouse, MapPinned, Plane, ScanBarcode, User } from 'lucide-react';
import PropTypes from 'prop-types';
import { useDisableTFA, useResendMail, useResendSumSubLink, useUpdatePostal } from './UserDetailapi';
import { useToast } from '../../hooks/usetoast';
import { WaitingLoader } from '../../hooks/buttonLoad';
import { Modal, ModalBody } from 'reactstrap';
import closecirImg from '../../assets/images/icons/close-circle.svg'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { queryClient } from '../../config';

const ProfileInfo = ({ userDetail }) => {

    const toast = useToast();
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("");
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openMail, setOpenMail] = useState(false);
    const resendLinkMutation = useResendSumSubLink()
    const reVerifyMailMutation = useResendMail()
    const disableTfaMutation = useDisableTFA();
    const updatePostalMutation = useUpdatePostal()
    const [postalCode, setPostalCode] = useState("");
    const [tfaStatus, setTfaStatus] = useState(userDetail?.data?.twoFactorEnabled);
    const [open, setOpen] = useState(false)
    const toggle = () => {
        setPostalCode("");
        setError("");
        setOpen(!open);
    };

    const validatePostalCode = (value) => {
        if (!value) return "Postal code is required";
        if (!/^\d+$/.test(value)) return "Only numeric characters allowed";
        if (value.length !== 6) return "Postal code must be exactly 6 digits";
        return "";
    };

    const isMailVerified = userDetail?.data?.isEmailVerified

    const handleConfirmOpen = () => setOpenConfirm(true);
    const handleConfirmClose = () => setOpenConfirm(false);

    const handleConfirmMail = () => setOpenMail(true);
    const handleCloseMail = () => setOpenMail(false);


    const handleResendSumsub = async () => {
        setLoading(true);
        try {
            const resp = await resendLinkMutation.mutateAsync({
                userId: userDetail?.data?._id,
            });
            if (!resp?.data?.status) {
                toast.error(resp?.data?.message);
            } else {
                toast.success(resp?.data?.message);
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message
            );
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmResend = () => {
        handleResendSumsub();
        handleConfirmClose();
    };

    const handleResendVerifyMail = async () => {
        setLoading(true);
        try {
            const resp = await reVerifyMailMutation.mutateAsync({
                id: userDetail?.data?._id,
            });
            if (!resp?.data?.status) {
                toast.error(resp?.data?.message);
            } else {
                toast.success(resp?.data?.message);
                queryClient.refetchQueries('single-users')
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message
            );
        } finally {
            setLoading(false);
        }
    };

    const handleMailResend = () => {
        handleResendVerifyMail();
        handleCloseMail();
    };



    const handlePostalCodeUpdate = async (e) => {
        e.preventDefault();
        const validationError = validatePostalCode(postalCode);
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        try {
            const resp = await updatePostalMutation.mutateAsync({
                userId: userDetail?.data?._id,
                postalCode
            });
            if (resp?.data?.status) {
                toast.success(resp?.data?.message);
                setPostalCode("");
                setError("");
                toggle();
            } else {
                toast.error(resp?.data?.message);
            }
        } catch (error) {
            console.error("Postal update error:", error);
            toast.error("Error updating postal code");
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async () => {
        if (!tfaStatus) return;
        try {
            const resp = await disableTfaMutation.mutateAsync({
                id: userDetail?.data?._id,
            });
            if (resp?.data?.status) {
                setTfaStatus(false);
                toast.success(resp?.data?.message)
                queryClient.refetchQueries('single-users')
            }
            else {
                toast.error(resp?.data?.message);
            }
        } catch (error) {
            console.error('Error disabling TFA:', error);
            toast.error('Failed to disable TFA.');
        }
    };

    return (
        <section>
            {loading && <WaitingLoader />}
            <div className="ad_pg_bx bg-white p-3">

                <div className='row'>
                    {/* Left Column */}
                    <div className='col-sm-6'>
                        <div className="text-center">
                            <label className="col-form-label mb-2" htmlFor="profileImage">Profile Image</label>
                            <div className="input-group my-3">
                                {userDetail?.data?.profileImage ? (
                                    <a
                                        href={userDetail?.data?.profileImage}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ cursor: 'pointer', display: 'inline-block' }}
                                        aria-label="Open profile image"
                                    >
                                        <img
                                            src={userDetail?.data?.profileImage}
                                            alt="User profile"
                                            style={{ height: '200px', width: '200px', objectFit: 'cover' }}
                                        />
                                    </a>
                                ) : (
                                    <div
                                        style={{
                                            height: '200px',
                                            width: '200px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f0f0f0',
                                            margin: 'auto',
                                        }}
                                    >
                                        <Camera size={40} color="#aaa9a9" />
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-between align-items-center text-center flex-wrap">
                                {/* Left Section */}
                                <div className="me-3">
                                    <label className="col-form-label mb-2" htmlFor="profileImage">Referred By</label>
                                    <div className="input-group my-3">
                                        <span style={{ fontWeight: '700' }}>
                                            {userDetail?.data?.referredBy?.userName}
                                            {userDetail?.data?.referredBy?.referralId && (
                                                <> ( ID {userDetail.data.referredBy.referralId} )</>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {/* Right Section */}
                                <div className="me-3">
                                    <span>TFA Status</span>
                                    <div className="form-check form-switch my-3 ms-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="tfaSwitch"
                                            checked={tfaStatus}
                                            onChange={handleToggle}
                                            disabled={userDetail?.data?.twoFactorEnabled === false}
                                        />
                                        <label className="form-check-label" htmlFor="tfaSwitch">
                                            {tfaStatus ? 'Enabled' : 'Disabled'}
                                        </label>
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>

                    {/* Right Column */}
                    <div className='col-sm-6 d-flex flex-column align-items-center justify-content-center'>

                        {!isMailVerified && (
                            <div className="mb-3 text-center">
                                <button className="btn btn-success" onClick={handleConfirmMail}>Resend Email Verify Link</button>
                            </div>
                        )}
                        {isMailVerified && (
                            <div className="mb-3 text-center">
                                <button className="btn btn-primary" onClick={handleConfirmOpen}>Resend Sumsub Link</button>
                            </div>
                        )}

                        {isMailVerified && (
                            <div className="text-center">
                                <button className="btn btn-info" onClick={() => toggle()}>Update Postal Code</button>
                            </div>
                        )}
                    </div>
                </div>



                <div className="row align-items-center justify-content-center" >

                    <div className="col-md-6">

                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="userName">
                                User Name
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <User size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="User Name"
                                        value={userDetail?.data?.userName || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="firstName">
                                First Name
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <User size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="First Name"
                                        value={userDetail?.data?.firstName || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="lastName">
                                Last Name
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <User size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Last Name"
                                        value={userDetail?.data?.lastName || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="email">
                                Email
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <Mail size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Email"
                                        value={userDetail?.data?.email || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="mobileNumber">
                                Mobile Number
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <span
                                        className="d-flex align-items-center px-2"
                                        style={{ background: 'none', border: 'none', fontSize: '1rem' }}
                                    >
                                        {userDetail?.data?.mobileCode || '91'}
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Mobile Number"
                                        value={userDetail?.data?.mobileNumber || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="country">
                                Country
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <Plane size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Country"
                                        value={userDetail?.data?.country || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="city">
                                City
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <MapPinned size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="City"
                                        value={userDetail?.data?.city || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="addressLine1">
                                Address Line
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <MapPinHouse size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Address Line"
                                        value={userDetail?.data?.addressLine1 || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row align-items-center mb-4">
                            <label className="col-sm-12 col-form-label" htmlFor="postalCode">
                                Postal Code
                            </label>
                            <div className="col-sm-12">
                                <div className="input-group">
                                    <button type="button" className="btn p-0 input-group-icon border-0">
                                        <ScanBarcode size={20} color="#aaa9a9" />
                                    </button>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Postal Code"
                                        value={userDetail?.data?.postalCode || '-'}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal isOpen={open} toggle={toggle} centered>
                <div className="modal-header">
                    <h1 className="modal-title fs-5">Update Postal Code</h1>
                    <button type="button" className="btn-close" onClick={toggle} aria-label="Close">
                        <img src={closecirImg} alt="close-circle" />
                    </button>
                </div>

                <ModalBody>
                    <form onSubmit={handlePostalCodeUpdate}>
                        <div className="mb-3">
                            <label htmlFor="postalCode" className="form-label">Postal Code</label>
                            <input
                                type="text"
                                id="postalCode"
                                name="postalCode"
                                className={`form-control ${error ? 'is-invalid' : ''}`}
                                maxLength={6}
                                inputMode="numeric"
                                required
                                value={postalCode}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*$/.test(value)) {
                                        setPostalCode(value);
                                        setError("");
                                    } else {
                                        setError("Only numeric characters allowed");
                                    }
                                }}
                            />
                            {error && <div className="invalid-feedback">{error}</div>}
                        </div>

                        <div className="text-end">
                            <button type="submit" className="btn btn-primary">Update</button>
                        </div>
                    </form>
                </ModalBody>
            </Modal>
            <Dialog
                open={openConfirm}
                onClose={handleConfirmClose}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    Confirm Action
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        Are you sure you want to resend the Sumsub link to this user?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmResend} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openMail}
                onClose={handleCloseMail}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    Confirm Action
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        Are you sure you want to resend the verification link to this user?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseMail} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleMailResend} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

        </section>
    );
};

ProfileInfo.propTypes = {
    userDetail: PropTypes.object.isRequired,
};

export default ProfileInfo;